import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { ApiPhysicalStoresOptions } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { ApiStoresService } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.service';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreSummary, DEFAULT_LANGUAGE_ID, PhysicalStoresOptions } from 'idt-ecommerce-api/stores/stores.models';

import { StoresModelsTransformer } from './stores.models.transformer';


@Injectable()
export class StoresService {

  constructor(
    private readonly apiStoresService: ApiStoresService,
    private readonly contextService: ContextService
  ) { }

  /**
   * Obtiene el listado de tiendas de una marca
   * @param languageId Identificador del idioma para el listado. Si no se cubre el idioma, y no está seteado en el
   * contexto, se asume el idioma por defecto (inglés).
   */
  public stores(languageId?: string): Observable<StoreSummary[]> {
    return this.contextService.getContext$().pipe(
      first(),
      switchMap((context: EcommerceContext | undefined) => {
        let selectedLanguageId: string;
        if (isDefined(context)) {
          selectedLanguageId = languageId || context.currentLanguage.id;
        } else {
          selectedLanguageId = languageId || DEFAULT_LANGUAGE_ID;
        }

        return this.apiStoresService.stores(selectedLanguageId).pipe(
          map((apiStoreList) => {
            const stores = StoresModelsTransformer.storesFromApi(apiStoreList.stores);
            // eslint-disable-next-line no-nested-ternary
            stores.sort((a, b) => (a.countryName < b.countryName ? -1 : a.countryName > b.countryName ? 1 : 0));
            return stores;
          })
        );
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public physicalStores(physicalStoresOptions: PhysicalStoresOptions, languageId?: string) {
    const options: ApiPhysicalStoresOptions = {
      favouriteStores: physicalStoresOptions.favouriteStores || true,
      lastStores: physicalStoresOptions.lastStores || false,
      closerStores: physicalStoresOptions.closerStores || true,
      latitude: physicalStoresOptions.latitude,
      longitude: physicalStoresOptions.longitude,
      min: physicalStoresOptions.minStores || 20,
      max: physicalStoresOptions.maxStores || 20,
      receiveEcommerce: physicalStoresOptions.receiveEcommerce || false
    };
    if (isDefined(physicalStoresOptions.countryCode)) {
      options.countryCode = physicalStoresOptions.countryCode;
    }
    if (isDefined(physicalStoresOptions.radioMax)) {
      options.radioMax = physicalStoresOptions.radioMax;
    }

    return this.contextService.getContext$().pipe(
      first(),
      switchMap((context: EcommerceContext) => this.apiStoresService.physicalStores(
        context.store.id,
        context.currentLanguage.id,
        options
      ).pipe(
        map((stores) => stores.closerStores.map(
          (store) => StoresModelsTransformer.physicalStoreFromApi(store, context)
        ))
      ))
    );
  }

}
